import { useMutation } from '@tanstack/react-query';
import { UserCheck } from 'lucide-react';
import { apiPost, tokenStore } from '@/lib/api';
import { useSession } from '@/features/auth/use-session';
import type { TokenPair } from '@/lib/types';

/** Decodes the access token payload without verifying it — display only. */
function readTokenClaims(): { imp?: string } | null {
  const token = tokenStore.access;
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { imp?: string };
  } catch {
    return null;
  }
}

/**
 * An unmissable banner while a super admin is signed in as someone else
 * (spec USR-06). Every action taken here is audit-logged against both people.
 */
export function ImpersonationBanner() {
  const { data: user } = useSession();
  const claims = readTokenClaims();

  const stop = useMutation({
    mutationFn: () => apiPost<TokenPair>('/auth/impersonate/stop'),
    onSuccess: (pair) => {
      tokenStore.set(pair);
      window.location.href = '/dashboard';
    },
  });

  if (!claims?.imp) return null;

  return (
    <div className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm text-amber-950">
      <span className="flex items-center gap-2 font-medium">
        <UserCheck className="h-4 w-4 shrink-0" />
        You are signed in as {user?.name ?? 'another user'}. Everything you do is logged.
      </span>
      <button
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
        className="shrink-0 rounded-md bg-amber-950/10 px-3 py-1 font-medium transition-colors hover:bg-amber-950/20 disabled:opacity-60"
      >
        {stop.isPending ? 'Returning…' : 'Return to my account'}
      </button>
    </div>
  );
}
